import { useState, useEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Target,
  Users,
  Shield,
  Building2,
  BookOpen,
  BarChart3,
  ChevronLeft,
  X,
  Spline,
  Route,
  FolderOpen,
  UserPlus,
  DoorOpen
} from 'lucide-react'
import clsx from 'clsx'
import Logo from '@/components/ui/Logo'
import { useAuth } from '@/providers/AuthProvider'
import { hasRole, hasAnyPermission, BUDGET_TAB_KEYS, ROLE_PROFILE_KEYS, STAFF_ORDER_KEYS } from '@/auth/permissions'
import { EXPORT_PERMISSION_KEYS } from '@/lib/exportPermissions'
import { WORKFORCE_ROLES, WORKFORCE_PERMISSION_KEYS } from '@/lib/workforcePermissions'
import { canSeeTalentDashboard } from '@/lib/talentDashboardPermissions'
import { documentsEnabled, hireRequestsEnabled, terminationRequestsEnabled } from '@/lib/featureFlags'
import { useManagerTeamSummary } from '@/hooks/manager/useManagerTeamSummary'
import { journeyForPath, sectionFromSearch, useJourneyNav, type Journey, type JourneyNavDetails } from '@/lib/journeySections'

const navigation = [
  { 
    name: 'Team Member Journey', 
    href: '/employee-journey', 
    icon: Route,
    description: ''
  },
  {
    name: 'People Leader Hub',
    href: '/people-leader-hub',
    icon: Users,
    description: ''
  },
  {
    name: 'Hire requests',
    href: '/hire-requests',
    icon: UserPlus,
    description: 'Fill a vacancy in your team, or ask for a new position'
  },
  {
    name: 'Resignations',
    href: '/termination-requests',
    icon: DoorOpen,
    description: 'Raise a resignation or retirement for someone in your team'
  },
  {
    name: 'My Documents',
    href: '/my-documents',
    icon: FolderOpen,
    // The sidebar shows only the text before the first comma as the subtitle
    // (the full sentence goes to the tooltip), so lead with the summary.
    description: 'Your employee documents, from payslips and tax certificates to contracts and qualifications'
  },
  {
    name: 'Organisation',
    href: '/organization',
    icon: Building2,
    description: 'Org structure, positions, and change management'
  },
  {
    name: 'Role Master',
    href: '/role-master',
    icon: BookOpen,
    description: 'Centralised role database and position classification'
  },
  {
    // Renamed from Workforce Explorer (HR, 9 Oct 2026); the sections underneath
    // are the Workforce Explorer itself and the talent dashboard.
    name: 'Dashboards',
    href: '/workforce-explorer',
    icon: BarChart3,
    description: 'Workforce and talent review dashboards'
  },
  {
    name: 'HR Admin',
    href: '/admin',
    icon: Shield,
    description: 'System administration, compliance, user management, and analytics'
  },
]



interface TooltipProps {
  content: string;
  children: React.ReactNode;
  side?: 'right' | 'left' | 'top' | 'bottom';
}

function SimpleTooltip({ content, children }: Omit<TooltipProps, 'side'>) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPosition({
        x: rect.right + 8, // 8px offset to the right
        y: rect.top + rect.height / 2
      })
    }
  }

  const handleMouseEnter = () => {
    setIsVisible(true)
    updatePosition()
  }

  return (
    <div 
      ref={triggerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div 
          className="fixed z-[9999] px-3 py-2 text-sm text-white bg-heritage-blue rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {content}
          <div 
            className="absolute w-2 h-2 bg-heritage-blue rotate-45 -left-1 top-1/2 -translate-y-1/2"
          />
        </div>
      )}
    </div>
  )
}

/**
 * A journey's sections, nested under its sidebar entry while that journey is
 * the current page: grouped with their live counts when the sidebar is open,
 * an icon column when it is collapsed. Each link is a real URL
 * (<journey>?tab=…) so the browser's back button and deep links work. The
 * links carry the guided tours' tab markers (data-demo for the hub's tour,
 * data-tour for the Team Member Journey's) and an active state for them.
 */
function JourneySectionTree({
  journey,
  collapsed,
  active,
  details,
  onNavigate,
}: {
  journey: Journey
  collapsed: boolean
  active: string
  details: JourneyNavDetails
  onNavigate?: () => void
}) {
  const link = (id: string) => ({ pathname: journey.path, search: `?tab=${id}` })
  const label = (id: string) => details.labels[id] || journey.sections[id].label
  const groups = journey.groups
    .map((g) => ({ ...g, items: g.items.filter((id) => !details.hidden.includes(id)) }))
    .filter((g) => g.items.length > 0)
  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 pt-1" aria-label={`${journey.title} sections`}>
        {groups.flatMap((g) => g.items).map((id) => {
          const Icon = journey.sections[id].icon
          const isActive = active === id
          return (
            <SimpleTooltip key={id} content={label(id)}>
              <NavLink
                to={link(id)}
                onClick={onNavigate}
                aria-current={isActive ? 'page' : undefined}
                data-demo={`tab-${id}`}
                data-tour={`tab-${id}`}
                data-state={isActive ? 'active' : 'inactive'}
                className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                  isActive ? 'bg-heritage-blue/15 text-heritage-blue' : 'text-gray-500 hover:bg-gray-100 hover:text-heritage-blue'
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </NavLink>
            </SimpleTooltip>
          )
        })}
      </div>
    )
  }
  return (
    <div className="ml-4 border-l border-gray-200/80 pl-2" aria-label={`${journey.title} sections`}>
      {groups.map((group) => (
        <div key={group.label} className="pt-1.5">
          <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{group.label}</p>
          <div className="space-y-0.5">
            {group.items.map((id) => {
              const Icon = journey.sections[id].icon
              const isActive = active === id
              return (
                <NavLink
                  key={id}
                  to={link(id)}
                  onClick={onNavigate}
                  aria-current={isActive ? 'page' : undefined}
                  data-demo={`tab-${id}`}
                  data-tour={`tab-${id}`}
                  data-state={isActive ? 'active' : 'inactive'}
                  className={clsx(
                    'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors',
                    isActive ? 'bg-heritage-blue/10 text-heritage-blue' : 'text-gray-600 hover:bg-gray-100 hover:text-heritage-blue'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className={clsx('block truncate text-xs', isActive ? 'font-semibold' : 'font-medium')}>{label(id)}</span>
                    {details.subtitles[id] && <span className="block truncate text-[10px] text-gray-500">{details.subtitles[id]}</span>}
                  </span>
                </NavLink>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

interface SidebarProps {
  isMobile?: boolean
  isOpen?: boolean
  onClose?: () => void
}

// Build time, formatted once. Falls back to the raw value rather than throwing
// if the injected string is ever not a date — the footer is not worth a crash.
const buildStamp = (() => {
  try {
    return new Date(__BUILD_TIME__).toLocaleDateString('en-ZA', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  } catch {
    return __BUILD_TIME__
  }
})()

export function Sidebar({ isMobile = false, isOpen = false, onClose }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { user } = useAuth()
  // A journey's sections show under its entry while that journey is the page.
  const location = useLocation()
  const currentJourney = journeyForPath(location.pathname)
  const currentSection = currentJourney ? sectionFromSearch(currentJourney, location.search) : ''
  const journeyDetails = useJourneyNav((state) => state.details)
  // Shared with the PL Hub page (same query key) so we don't fire a second
  // team-summary request just to decide whether to show the People Leader nav.
  const teamSummaryQuery = useManagerTeamSummary(user?.employeeId, new Date().getFullYear())
  const hasDirectReports: boolean | null = !user?.employeeId
    ? false
    : teamSummaryQuery.data
      ? teamSummaryQuery.data.team_members.length > 0
      : null

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    if (saved !== null) {
      setIsCollapsed(JSON.parse(saved))
    } else {
      // Auto-collapse on narrow viewports (laptops and high DPI scenarios)
      const shouldAutoCollapse = window.innerWidth < 1500
      if (shouldAutoCollapse) {
        setIsCollapsed(true)
        localStorage.setItem('sidebar-collapsed', JSON.stringify(true))
      }
    }
  }, [])

  // Save collapsed state to localStorage
  const toggleCollapsed = () => {
    const newState = !isCollapsed
    setIsCollapsed(newState)
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState))
  }

  // In mobile drawer mode, force expanded
  const effectiveCollapsed = isMobile ? false : isCollapsed

  const sidebarContent = (
    <div 
      className={clsx(
        'flex flex-col bg-gradient-to-b from-white via-gray-50/30 to-white border-r border-gray-200/80 shadow-xl transition-all duration-300 ease-in-out relative',
        isMobile
          ? 'w-64 h-full'
          : (isCollapsed ? 'w-14 sm:w-16' : 'w-48 sm:w-52 md:w-56 lg:w-60 xl:w-64 max-w-[15vw]')
      )}
    >
      {/* Decorative gradient accent */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-heritage-blue via-future-blue to-heritage-blue opacity-60"></div>

      {/* Header with Logo and Collapse/Close Toggle */}
      <div className={clsx(
        "relative h-16 px-4 border-b border-gray-200/80 overflow-hidden",
        "bg-gradient-to-br from-heritage-blue/10 via-future-blue/5 to-transparent"
      )}>
        {/* Decorative background blur */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-future-blue/20 rounded-full blur-3xl"></div>
        
        <div className="relative h-full flex items-center justify-between">
          {isMobile ? (
            <>
              <div className="flex items-center justify-center flex-1">
                <Logo variant="horizontal" size="md" />
              </div>
              <button
                onClick={onClose}
                className={clsx(
                  'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                  'hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-heritage-blue/30 shadow-sm',
                  'text-heritage-blue/70 hover:text-heritage-blue hover:shadow-md backdrop-blur-sm',
                  'border border-heritage-blue/10'
                )}
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : effectiveCollapsed ? (
            <button
              onClick={toggleCollapsed}
              className="flex items-center justify-center w-full h-full focus:outline-none focus:ring-2 focus:ring-heritage-blue/30 hover:opacity-80 transition-opacity"
              aria-label="Expand sidebar"
            >
              <Logo variant="icon-only" size="sm" />
            </button>
          ) : (
            <>
              <div className="flex items-center justify-center flex-1">
                <Logo variant="horizontal" size="md" />
              </div>
              
              <button
                onClick={toggleCollapsed}
                className={clsx(
                  'flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200',
                  'hover:bg-white/80 focus:outline-none focus:ring-2 focus:ring-heritage-blue/30 shadow-sm',
                  'text-heritage-blue/70 hover:text-heritage-blue hover:shadow-md backdrop-blur-sm',
                  'border border-heritage-blue/10'
                )}
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
        {/* Main Navigation */}
        {navigation
          .filter((item) => {
            // Hide the People Leader Hub from someone with no direct reports —
            // unless they are HR, who sit in Annual I&G Review sessions from its
            // Talent tab without leading anyone (HR process owner, 8 Sep 2026).
            if (item.href === '/people-leader-hub' && hasDirectReports === false) {
              return user?.roles?.some(role => role === 'admin' || role === 'hr_admin' || role === 'hrbp') === true
            }
            // My Documents: only in a build whose environment has the documents API
            // (VITE_DOCUMENTS_ENABLED) — the route in App.tsx is gated the same way.
            if (item.href === '/my-documents') {
              return documentsEnabled()
            }
            // Hire requests: only in a build that carries VITE_HIRE_REQUESTS_ENABLED
            // (the route in App.tsx is gated the same way), and only for someone who
            // leads a team. The server decides who may raise one.
            if (item.href === '/hire-requests') {
              return hireRequestsEnabled() && hasDirectReports === true
            }
            // Resignations: People Leaders see it here; everyone else reaches their
            // own resignation from My Documents, not the main menu.
            if (item.href === '/termination-requests') {
              return terminationRequestsEnabled() && hasDirectReports === true
            }
            // Organization: only full admin/hr_admin
            if (item.href === '/organization') {
              return user?.roles?.some(role =>
                role === 'admin' || role === 'hr_admin'
              ) === true
            }
            // Role Master: admin/hr_admin, or anyone granted Budget or role-profile
            // access — without this a grantee has no way to REACH the tab they were
            // given (the page gates itself; this is just the way in).
            if (item.href === '/role-master') {
              return user?.roles?.some(role =>
                role === 'admin' || role === 'hr_admin'
              ) === true || hasAnyPermission(user, BUDGET_TAB_KEYS) || hasAnyPermission(user, ROLE_PROFILE_KEYS)
            }
            // Workforce Explorer: the org-console roles, or anyone granted the
            // dashboard (or just its export) — same condition WorkforceRoute
            // decides on, so the nav item and the page never disagree. Note the
            // keys are NOT 'export:'-prefixed, so this grant leaks no /admin
            // visibility via the branch below.
            if (item.href === '/workforce-explorer') {
              return hasRole(user, WORKFORCE_ROLES) || hasAnyPermission(user, WORKFORCE_PERMISSION_KEYS) || canSeeTalentDashboard(user)
            }
            // HR Admin: the admin roles, or anyone granted a single export or the staff order.
            if (item.href === '/admin') {
              return user?.roles?.some(role =>
                role === 'admin' || role === 'hr_admin' || role === 'hrbp' || role === 'fno_admin' || role === 'data_team'
              ) === true || hasAnyPermission(user, EXPORT_PERMISSION_KEYS) || hasAnyPermission(user, STAFF_ORDER_KEYS)
            }
            return true
          })
          .map((item) => {
          const NavContent = (
            <NavLink
              key={item.name}
              // HR without a team goes straight to the Talent tab, where their sessions are.
              to={item.href === '/people-leader-hub' && hasDirectReports === false ? '/people-leader-hub?tab=talent' : item.href}
              onClick={isMobile ? onClose : undefined}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 relative overflow-hidden',
                  'hover:scale-[1.02] active:scale-[0.98]',
                  isActive
                    ? 'bg-gradient-to-br from-heritage-blue to-heritage-blue/90 text-white shadow-lg shadow-heritage-blue/25'
                    : 'text-gray-700 hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100/50 hover:text-heritage-blue hover:shadow-md',
                  effectiveCollapsed ? 'justify-center' : ''
                )
              }
            >
              {/* Subtle shine effect on active */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              
              <item.icon
                className={clsx(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200 relative z-10',
                  effectiveCollapsed ? '' : 'mr-3',
                  'group-hover:scale-110'
                )}
                aria-hidden="true"
              />
              {!effectiveCollapsed && (
                <div className="flex-1 flex flex-col relative z-10">
                  <span className="truncate font-semibold">
                    {item.name}
                  </span>
                  <span className="text-[10px] opacity-80 truncate font-normal">
                    {item.description.split(',')[0]}
                  </span>
                </div>
              )}
            </NavLink>
          )

          const rendered = effectiveCollapsed ? (
            <SimpleTooltip key={item.name} content={`${item.name} - ${item.description}`}>
              {NavContent}
            </SimpleTooltip>
          ) : NavContent

          if (currentJourney && item.href === currentJourney.path) {
            return (
              <div key={item.name} className="space-y-1">
                {rendered}
                <JourneySectionTree
                  journey={currentJourney}
                  collapsed={effectiveCollapsed}
                  active={currentSection}
                  details={journeyDetails[currentJourney.id]}
                  onNavigate={isMobile ? onClose : undefined}
                />
              </div>
            )
          }
          return rendered
        })}

      </nav>

      {/* User Context Footer */}
      {!effectiveCollapsed && (
        <div className="relative px-4 py-4 border-t border-gray-200/80 bg-gradient-to-br from-heritage-blue/5 to-future-blue/10 overflow-hidden">
          {/* Decorative background */}
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-future-blue/10 rounded-full blur-2xl"></div>
          
          {/* Which build is this? The old footer answered "v1.2.0 / 2026",
              hardcoded, matching no artefact and unchanged across every deploy.
              These come from the build itself, so the answer is checkable.

              The "System Online" dot that sat above is gone. It was static
              markup with no health check behind it — it stayed green while
              everything was down, which is worse than showing nothing, because
              someone reporting a fault would read it as evidence. */}
          <div className="relative flex items-center justify-between text-[10px] text-heritage-blue/60"
            title={`Build ${buildStamp}${__BUILD_SHA__ ? ` · commit ${__BUILD_SHA__}` : ''}`}>
            <span>v{__APP_VERSION__}</span>
            <span className="font-medium tabular-nums">
              {__BUILD_SHA__ || buildStamp}
            </span>
          </div>
        </div>
      )}
    </div>
  )

  // Mobile drawer mode: render with overlay backdrop
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300"
            onClick={onClose}
            aria-hidden="true"
          />
        )}
        {/* Drawer */}
        <div
          className={clsx(
            'fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out',
            isOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {sidebarContent}
        </div>
      </>
    )
  }

  // Desktop: render inline as before
  return sidebarContent
}
